import { useFeatures } from "@/api/app-configuration.api";
import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { RadioCard } from "@/components/generic/RadioCard";
import {
  NewCampaignState,
  newCampaignStateAtom,
} from "@/services/new-campaign.service";
import { deliveryTypes } from "@/types/campaign";
import { store } from "@/utils/jotai";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { RadioGroup } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import z from "zod";

export const Route = createFileRoute(
  "/_dashboard/campaigns/setup/delivery/method",
)({
  component: RouteComponent,
  staticData: {
    meta: {
      title: "Destination",
    },
  },
  loader: () => ({
    hideSubmitButton: !store.get(newCampaignStateAtom).delivery,
  }),
});

const formSchema = z.object({
  type: z
    .enum(deliveryTypes.map((m) => m.type))
    .nullable()
    .refine((value) => value !== null, "Vælg en destination"),
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const stepper = context.useStepper();

  const { canUseIntegration } = useFeatures();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: stepper.context.delivery?.type ?? null,
    },
  });

  const submit = useMemo(
    () =>
      handleSubmit(({ type }) => {
        if (type === null) throw Error("No type selected");

        stepper.setContext((prev) => {
          if (type === prev.delivery?.type) return prev;

          let newDelivery: NewCampaignState["delivery"];

          switch (type) {
            case "LassoSales":
              newDelivery = {
                type,
              };
              break;

            case "Excel":
              newDelivery = {
                type,
                completionEmail: null,
              };
              break;

            case "Integration":
              newDelivery = {
                type,
                exportSystem: null,
                exportInput: null,
                exportMode: null,
              };
              break;
          }

          const newStepperContext = {
            ...prev,
            delivery: newDelivery,
          };

          const contactInformationStep =
            stepper.flatStepsById["planning/contact-information"];

          // If this update makes the contact information step invisible, we need to reset the contact information fields from the context.
          if (
            !contactInformationStep.getIsVisible(newStepperContext) &&
            contactInformationStep.getIsVisible(prev)
          ) {
            newStepperContext.planning = {
              ...prev.planning,
              deliverContacts: null,
              deliverContactsRequirePhone: null,
              deliverLiveNumber: null,
            };
          }

          return newStepperContext;
        });

        stepper.goNext();
      }),
    [handleSubmit, stepper],
  );

  context.stepper.useStepperSubmit(
    useMemo(() => ({ submit, isDisabled: !isValid }), [submit, isValid]),
  );

  return (
    <form className="flex flex-col gap-y-10" onSubmit={submit}>
      <SegmentHeader
        className="mx-auto max-w-lg"
        title="Vælg destination"
        description="Her vælger du hvor du ønsker at modtage dine leads. Dine leads vil altid være tilgængelige i Lasso Sales, uanset hvad du vælger."
      />

      <Controller
        control={control}
        name="type"
        render={({ field }) => (
          <RadioGroup
            className="flex grow justify-center gap-x-6"
            value={field.value}
            onChange={field.onChange}
            aria-label="Vælg destination"
          >
            {deliveryTypes.map((method) => (
              <RadioCard
                key={method.type}
                className="flex size-full max-w-sm flex-col items-center justify-center gap-y-6 rounded-lg p-6 xl:p-12"
                value={method.type}
                isDisabled={method.type === "Integration" && !canUseIntegration}
                onPress={() => {
                  field.onChange(method.type);
                  submit();
                }}
                aria-label={method.name}
              >
                <div className="text-theme-gradient-to bg-theme-gradient-to/30 border-theme-gradient-to/40 flex size-12 items-center justify-center rounded-full border">
                  <method.icon className="size-6" />
                </div>

                <div className="flex max-w-48 flex-col gap-y-4">
                  <div className="text-center text-base/normal font-semibold">
                    {method.name}
                  </div>

                  <p className="text-theme-on-background-variant max-w-md text-center text-sm/normal">
                    {method.description}
                  </p>
                </div>
              </RadioCard>
            ))}
          </RadioGroup>
        )}
      />
    </form>
  );
}
